
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from "@/integrations/supabase/client";
import AdminHeader from '@/components/admin/AdminHeader';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import AppSettingsForm from '@/components/admin/AppSettingsForm';
import AdminPageLoader from '@/components/admin/AdminPageLoader';
import { useAppSettingsForm } from '@/hooks/useAppSettingsForm';
import { useAppSettings } from '@/hooks/useAppSettings';
import GameCounterReset from '@/components/admin/GameCounterReset';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const AdminSettings = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('branding');
  
  const {
    settings,
    saving,
    handleSettingsChange,
    saveSettings,
    isFormDirty,
    saveSuccess,
    hasChanges
  } = useAppSettingsForm();
  
  // Use the app settings hook to ensure UI is updated after changes
  const { updatePwaAssets } = useAppSettings();

  // Update PWA assets after successful save
  useEffect(() => {
    if (saveSuccess) {
      updatePwaAssets();
    }
  }, [saveSuccess, updatePwaAssets]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (!user) {
          navigate('/login');
          return;
        }

        // Check if user has admin privileges
        const { data, error } = await supabase
          .from('customer_profiles')
          .select('tier')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (data.tier !== 'admin') {
          toast({
            title: t('admin:accessDenied'),
            description: t('admin:notAuthorized'),
            variant: 'destructive',
          });
          navigate('/dashboard');
        } else {
          setIsAdmin(true);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user, navigate, toast, t]);

  if (loading) {
    return <AdminPageLoader />;
  }

  if (!isAdmin) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8F7F5] to-[#F5EBD8]/30">
      <AdminHeader />
      
      <main className="container mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold mb-6">{t('admin:appSettings')}</h1>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-2xl mx-auto">
          <TabsList className="mb-4 grid grid-cols-2 gap-2">
            <TabsTrigger value="branding">{t('admin:branding', 'Branding')}</TabsTrigger>
            <TabsTrigger value="tools">{t('admin:tools', 'Admin Tools')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="branding">
            <AppSettingsForm 
              settings={settings}
              saving={saving}
              onSave={saveSettings}
              onSettingsChange={handleSettingsChange}
              isFormDirty={isFormDirty}
              saveSuccess={saveSuccess}
              hasChanges={hasChanges()}
            />
          </TabsContent>
          
          <TabsContent value="tools">
            <GameCounterReset />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminSettings;
